// React Dependencies
import React, { Component, createRef } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, Redirect } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box, Button, FormControl, InputAdornment, InputLabel, MenuItem, Select, TextField } from '@material-ui/core';
import MaterialTable from 'material-table';
import Search from '@material-ui/icons/Search';
import _ from 'lodash';

// Internal Dependencies
import apiService from '../../../services.js/apiService';
import { updateModalContent, toggleModal } from '../../../actions/modal';
import { IsEmpty, IsTrue } from '../../../common/checks';
import { formatDateToDayMonthNameYear, showDateInSGTime, ElipsisString, getThumbnailFromVideoURL } from '../../../common/util';
import { Colours, Status } from '../../../common/constants';

const pageOptions = [5, 10, 15, 20, 25];
class PostTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      posts:            [],
      totalCount:       0,
      redirUrl:         null,
      pageSize:         pageOptions[1],
      search:           '', //the searchBar component state
      searchQuery:      '', //the table query search state
      status:           '',
      searchableStatus: '',
      searchFocus:      false,
    };

    this.tableRef = createRef();

    this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.clearFilter = this.clearFilter.bind(this);
  }

   handleRedirect = (url) => {
    this.setState({ redirUrl: url });
   }

  handleDelete = (id) => {
    this.props.updateModalContent({
      title:        'Are you sure?',
      content:      'All data related to this post will be deleted!',
      actionButton: {
        actionText: 'Yes, Delete',
        actionType: 'danger',
        action:     async () => {
          try {
            await apiService.deletePost(id);
            toast.success('Post deleted');
            const posts = this.state.posts.filter((post) => post.id !== id);
            this.setState({ posts });
            this.tableRef.current.onQueryChange();
          } catch (error) {
            toast.error(error.message);
          }
        },
      },
      cancelText:       'No',
      closeAfterAction: true,
    });
    this.props.toggleModal();
  }

  clearFilter() {
    this.setState({
      search:           '',
      searchQuery:      '',
      status:           '',
      searchableStatus: '',
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.status !== this.state.status
      || prevState.searchableStatus !== this.state.searchableStatus
    ) {
      this.tableRef.current.onQueryChange();
    }
  }

  handleChangeRowsPerPage = (pageSize) => {
    this.setState({ pageSize });
  };

  debounceSearchQuery=_.debounce((text) => this.setState({ searchQuery: text }, () => this.tableRef.current.onQueryChange()), 500);

  handleSearchChange = (e) => {
    this.setState({
      search:      e.target.value,
      searchFocus: true,
    });
    this.debounceSearchQuery(e.target.value);
  };

  render() {
    if (this.state.redirUrl) { return <Redirect to={this.state.redirUrl} />; }
    return (
      <MaterialTable
        tableRef={this.tableRef}
        title=""
        columns={[
          {
            title:  'Id', field:  'id', render: (post) => {
              const postId = !IsEmpty(post.postId) ? post.postId : post.id;
              return (
                <Link to={`/admin/page/${this.props.pageId}/post/${postId}${!IsEmpty(this.props.pageHighlightId) ? `?pageHighlightId=${this.props.pageHighlightId}` : ''}`}>
                  {postId}
                </Link>
              );
            },
            width: '5%',
          },
          {
            title:  'Image', field:  'image', render: (post) => {
              const isVideo = !!(!IsEmpty(post.videos) && post.videos.length > 0);
              const media = isVideo
                ? (
                  <img
                    src={getThumbnailFromVideoURL(post.videos[0])}
                    alt=""
                  />
                )
                : (post.images && post.images.length > 0 && post.images[0].display) ? <img src={post.images[0].original} alt="" style={{ maxWidth: '100px', resizeMode: 'contain' }} /> : '';

              return media;
            },
            sorting: false,
            width:   '10%',
          },
          {
            title:  'Title', field:  'title', render: (post) => {
              // Exclusive highlight post has its post id saved in 'postId'
              const postId = !IsEmpty(post.postId) ? post.postId : post.id;
              return (
                <Link to={`/admin/page/${this.props.pageId}/post/${postId}${!IsEmpty(this.props.pageHighlightId) ? `?pageHighlightId=${this.props.pageHighlightId}` : ''}`}>
                  {post.title}
                </Link>
              );
            },
            width: '20%',
          },
          { title: 'Type', field: 'type', width: '7.5%' },
          { title: 'Status', field: 'status', width: '7.5%' },
          { title: 'Searchable Status', field: 'searchableStatus', width: '7.5%' },
          {
            title:  'Post Date',
            field:  'postDate',
            render: (post) => (post.postDate !== null ? `${formatDateToDayMonthNameYear(showDateInSGTime(post.postDate))}` : '-'),
            width:  '10%',
          },
          {
            title:  'Start Date',
            field:  'promoFromDate',
            render: (post) => (post.promoFromDate !== null ? `${formatDateToDayMonthNameYear(showDateInSGTime(post.promoFromDate))}` : '-'),
            width:  '10%',
          },
          {
            title:  'End Date',
            field:  'promoTillDate',
            render: (post) => (post.promoTillDate !== null ? `${formatDateToDayMonthNameYear(showDateInSGTime(post.promoTillDate))}` : '-'),
            width:  '10%',
          },
          {
            title:  'Page Highlight(s)', field:  'pageHighlights', render: (post) => (
              <div style={{ display: 'grid' }}>
                {_.map(post.pageHighlights, (pageHighlight) => (
                  <Link
                    className="mb-2"
                    target="_blank"
                    to={`/admin/page/${this.props.pageId}/highlight/${pageHighlight.id}${IsTrue(pageHighlight.isExclusive) ? `?isExclusive=true` : `?isSpecial=true`}`}
                  >
                    <div
                      className="px-3 py-2"
                      style={{
                        backgroundColor: pageHighlight.isSpecial ? Colours.Grey : Colours.Gold,
                        borderRadius:    15,
                      }}
                    >
                      <p style={{ color: pageHighlight.isExclusive ? Colours.Black : Colours.White }}>
                        {ElipsisString(pageHighlight.title, 25)}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ),
            width: '30%',
          },
          { title: 'Last Updated By', field: 'lastUpdatedBy', width: '10%' },
        ]}
        components={{ Toolbar: () => (
          <div className="pt-3">
            <Box className="pb-0" display="flex" justifyContent="flex-start" m={0} p={1} bgcolor="background.paper">
              <Box width="15%">
                <FormControl fullWidth>
                  <Button variant="outlined" size="medium" color="secondary" onClick={this.clearFilter}>Clear Filters</Button>
                </FormControl>
              </Box>
            </Box>
            <Box display="flex" justifyContent="flex-start" m={0} p={1} bgcolor="background.paper">
              <Box p={1} width="23%">
                <FormControl fullWidth>
                  <TextField
                    label="Search Here "
                    id="standard-adornment-amount"
                    placeholder="Search by Title"
                    value={this.state.search}
                    onChange={this.handleSearchChange}
                    type="search"
                    startAdornment={(
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    )}
                    autoFocus={this.state.searchFocus}
                    alignSelf="flex-end"
                  />
                </FormControl>
              </Box>
              <Box p={1} width="15%">
                <FormControl fullWidth>
                  <InputLabel id="status-filter-label">Status</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    value={this.state.status}
                    name="postStatus"
                    onChange={(e) => this.setState({ status: e.target.value })}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value={Status.Draft}>{Status.Draft}</MenuItem>
                    <MenuItem value={Status.Pending}>{Status.Pending}</MenuItem>
                    <MenuItem value={Status.Live}>{Status.Live}</MenuItem>
                    <MenuItem value={Status.Rejected}>{Status.Rejected}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box p={1} width="15%">
                <FormControl fullWidth>
                  <InputLabel id="status-filter-label">Searchable Status</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    value={this.state.searchableStatus}
                    name="postSearchableStatus"
                    onChange={(e) => this.setState({ searchableStatus: e.target.value })}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value={Status.Live}>{Status.Live}</MenuItem>
                    <MenuItem value={Status.Pending}>{Status.Pending}</MenuItem>
                    <MenuItem value={Status.Expired}>{Status.Expired}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </div>
        ) }}
        onChangeRowsPerPage={this.handleChangeRowsPerPage}
        data={(query) => new Promise(async (resolve, reject) => {
          try {
            query.pageNo = query.page;
            query.order = { col: 'searchableStatus', dir: 'asc' }; // default sort is ascending searchable status
            query.title = this.state.searchQuery;
            query.status = this.state.status;
            query.searchableStatus = this.state.searchableStatus;
            if (!IsEmpty(query.orderDirection) && !IsEmpty(query.orderBy)) {
              query.order = { col: query.orderBy.field, dir: query.orderDirection };
            }
            let { page } = query;

            if (!IsEmpty(this.props.pageHighlightId)) {
              const result = await apiService.getPostPageHighlights({
                ...query,
                pageId:          this.props.pageId,
                pageHighlightId: this.props.pageHighlightId,
              });
              resolve({
                data:       result.rows,
                page,
                totalCount: result.totalCount,
              });
            } else {
              const result = await apiService.getPostsByParamsForPage({ ...query,  isIncludePageHighlight: true }, this.props.pageId);
              let { rows, totalCount } = result;
              if (rows.length === 0) {
                page = 0;

                if (totalCount !== 0) {
                  const resultFirstPage = await apiService.getPostsByParamsForPage({ ...query, page, isIncludePageHighlight: true }, this.props.pageId);
                  rows = resultFirstPage.rows;
                }
              }
              resolve({
                data: rows,
                page,
                totalCount,
              });
            }
          } catch (err) {
            toast.error(err.message);
            resolve({
              data:       [],
              page:       0,
              totalCount: 0,
            });
          }
        })}
        actions={[
          {
            icon:    'edit',
            tooltip: 'View/Edit Post',
            onClick: (event, post) => {
              // Exclusive highlight post has its post id saved in 'postId'
              const postId = !IsEmpty(post.postId) ? post.postId : post.id;
              this.handleRedirect(`/admin/page/${this.props.pageId}/post/${postId}${!IsEmpty(this.props.pageHighlightId) ? `?pageHighlightId=${this.props.pageHighlightId}` : ''}`);
            },
          },
          {
            icon:    'delete',
            tooltip: 'Delete Post',
            onClick: (event, post) => {
              // Exclusive highlight post has its post id saved in 'postId'
              const postId = !IsEmpty(post.postId) ? post.postId : post.id;
              this.handleDelete(postId);
            },
          },
        ]}
        options={{
          actionsColumnIndex:  -1,
          sorting:             true,
          paging:              true,
          emptyRowsWhenPaging: false,
          pageSize:            this.state.pageSize,
          pageSizeOptions:     pageOptions,
        }}

      />
    );
  }
}

PostTable.propTypes = {
  pageId:             PropTypes.number.isRequired,
  pageHighlightId:    PropTypes.number,
  toggleModal:        PropTypes.func.isRequired,
  updateModalContent: PropTypes.func.isRequired,
};

PostTable.defaultProps = {
  pageHighlightId: null,
};

const mapDispatchToProps = (dispatch) => ({
  toggleModal:        () => dispatch(toggleModal()),
  updateModalContent: (data) => dispatch(updateModalContent(data)),
});

export default connect(null, mapDispatchToProps)(PostTable);
